<template>
  <div class="performance-snapshot card shadow-sm border-0 mb-3">
    <div class="card-body py-2 px-2 px-md-3">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <div class="snapshot-title">
          <i class="bi bi-speedometer2 me-1"></i>
          Performance Snapshot
        </div>
        <button class="btn btn-sm btn-outline-info py-0 px-2" @click="openInfoModal">
          <i class="bi bi-info-circle me-1"></i>
          Info
        </button>
      </div>

      <div v-if="errorMessage" class="snapshot-error text-danger">
        {{ errorMessage }}
      </div>

      <div class="snapshot-scroll-wrap">
        <div class="snapshot-row">
          <template v-if="loading">
            <div v-for="index in 5" :key="`snapshot-skeleton-${index}`" class="snapshot-tile">
              <div class="skeleton-line skeleton-title"></div>
              <div class="skeleton-line skeleton-value"></div>
              <div class="skeleton-line skeleton-sub"></div>
              <div class="skeleton-line skeleton-sub short"></div>
            </div>
          </template>

          <template v-else>
            <div v-for="tile in tiles" :key="tile.key" class="snapshot-tile">
              <div class="tile-label">{{ tile.label }}</div>
              <div class="tile-value">{{ tile.value }}</div>
              <div class="tile-subtext" :title="tile.secondaryTitle || null">{{ tile.secondary }}</div>
              <div v-if="tile.tertiary" class="tile-subtext">{{ tile.tertiary }}</div>
              <div class="tile-icon-shell" :class="tile.iconTone" aria-hidden="true">
                <i class="bi" :class="tile.icon"></i>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>

    <BootstrapModal
      ref="infoModal"
      modalId="performanceSnapshotInfoModal"
      title="Methodology"
      :isLarge="true"
      :hideConfirmButton="true"
    >
      <template #body>
        <div class="snapshot-modal-body">
          <div class="fw-semibold mb-3">Performance Snapshot</div>

          <div class="info-section">
            <h6 class="mb-2">Formula</h6>
            <ul v-if="calculationRules.length" class="mb-0 ps-3">
              <li v-for="(rule, index) in calculationRules" :key="`rule-${index}`">
                {{ rule }}
              </li>
            </ul>
            <div v-else class="small text-muted">Calculation details are not available</div>
          </div>

          <div class="info-section">
            <h6 class="mb-2">Comparison Range</h6>
            <div class="small">
              {{ previousPeriodText }}
            </div>
          </div>

          <div class="info-section">
            <h6 class="mb-2">Audit</h6>
            <div class="small mb-1">
              <span class="text-muted">Timezone:</span>
              <span class="ms-1">{{ stringifyValue(audit?.timezone) }}</span>
            </div>
            <div class="small mb-1">
              <span class="text-muted">Start at:</span>
              <span class="ms-1">{{ stringifyValue(audit?.date_window?.start_at) }}</span>
            </div>
            <div class="small mb-1">
              <span class="text-muted">End before:</span>
              <span class="ms-1">{{ stringifyValue(audit?.date_window?.end_before) }}</span>
            </div>
            <div class="small">
              <span class="text-muted">Formula version:</span>
              <span class="ms-1">{{ stringifyValue(audit?.formula_version) }}</span>
            </div>
          </div>
        </div>
      </template>
    </BootstrapModal>
  </div>
</template>

<script>
import BootstrapModal from "@/components/Common/BootstrapModal.vue";

export default {
  name: "PerformanceSnapshotBar",
  components: {
    BootstrapModal,
  },
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    errorMessage: {
      type: String,
      default: "",
    },
    snapshot: {
      type: Object,
      default: null,
    },
    trendIndicator: {
      type: Object,
      default: null,
    },
    calculation: {
      type: Object,
      default: null,
    },
    audit: {
      type: Object,
      default: null,
    },
  },
  computed: {
    revenueTrendMetric() {
      return this.trendIndicator?.metrics?.revenue_total || null;
    },
    revenueTrendText() {
      const metric = this.revenueTrendMetric;
      if (!metric) {
        return "No previous baseline";
      }

      if (metric.delta_pct === null || metric.delta_pct === undefined) {
        return "No previous baseline";
      }

      return `${this.getArrow(metric.direction)} ${this.formatSignedDelta(metric.delta_pct)}% vs previous period`;
    },
    safeSnapshot() {
      return this.snapshot || {};
    },
    tiles() {
      return [
        {
          key: "total_revenue",
          label: "Total Revenue",
          value: this.formatMoney(this.safeSnapshot.total_revenue, { fallbackToZero: true }),
          secondary: this.revenueTrendText,
          secondaryTitle: this.previousPeriodText,
          tertiary: "",
          icon: "bi-cash-stack",
          iconTone: "tile-icon-tone-revenue",
        },
        {
          key: "best_device",
          label: "Best Device",
          value: this.safeSnapshot.best_device?.device_name || "N/A",
          secondary: `${this.formatMoney(this.safeSnapshot.best_device?.revenue, { fallbackToZero: false })}`,
          secondaryTitle: "",
          tertiary: this.renderPhotosText(this.safeSnapshot.best_device?.photos),
          icon: "bi-trophy",
          iconTone: "tile-icon-tone-best",
        },
        {
          key: "worst_device",
          label: "Worst Device",
          value: this.safeSnapshot.worst_device?.device_name || "N/A",
          secondary: `${this.formatMoney(this.safeSnapshot.worst_device?.revenue, { fallbackToZero: false })}`,
          secondaryTitle: "",
          tertiary: this.renderPhotosText(this.safeSnapshot.worst_device?.photos),
          icon: "bi-exclamation-triangle",
          iconTone: "tile-icon-tone-worst",
        },
        {
          key: "best_day",
          label: "Best Day",
          value: this.safeSnapshot.best_day?.date || "N/A",
          secondary: `${this.formatMoney(this.safeSnapshot.best_day?.revenue, { fallbackToZero: false })}`,
          secondaryTitle: "",
          tertiary: this.renderPhotosText(this.safeSnapshot.best_day?.photos),
          icon: "bi-calendar2-check",
          iconTone: "tile-icon-tone-day",
        },
        {
          key: "peak_hour",
          label: "Peak Hour",
          value: this.safeSnapshot.peak_hour?.label || "N/A",
          secondary: `${this.formatMoney(this.safeSnapshot.peak_hour?.revenue, { fallbackToZero: false })}`,
          secondaryTitle: "",
          tertiary: this.renderPhotosText(this.safeSnapshot.peak_hour?.photos),
          icon: "bi-clock-history",
          iconTone: "tile-icon-tone-hour",
        },
      ];
    },
    calculationRules() {
      if (!this.calculation?.rules) {
        return [];
      }

      if (Array.isArray(this.calculation.rules)) {
        return this.calculation.rules;
      }

      return [String(this.calculation.rules)];
    },
    previousPeriodText() {
      const previous = this.trendIndicator?.previous_period;
      if (!previous?.from || !previous?.to) {
        return "Compared to previous period: N/A";
      }

      return `Compared to previous period: ${previous.from} → ${previous.to}`;
    },
  },
  methods: {
    openInfoModal() {
      this.$refs.infoModal?.show();
    },
    getArrow(direction) {
      if (direction === "up") {
        return "▲";
      }

      if (direction === "down") {
        return "▼";
      }

      return "■";
    },
    formatSignedDelta(value) {
      if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return "N/A";
      }

      const numeric = Number(value);

      if (numeric > 0) {
        return `+${numeric.toFixed(2)}`;
      }

      if (numeric < 0) {
        return `-${Math.abs(numeric).toFixed(2)}`;
      }

      return numeric.toFixed(2);
    },
    formatMoney(value, { fallbackToZero = true } = {}) {
      if (
        (value === null || value === undefined || value === "") &&
        !fallbackToZero
      ) {
        return "N/A";
      }

      const numeric = Number(value || 0);
      return `${numeric.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} GEL`;
    },
    renderPhotosText(value) {
      if (value === null || value === undefined) {
        return "";
      }

      return `${Math.round(Number(value || 0)).toLocaleString()} photos`;
    },
    stringifyValue(value) {
      if (value === null || value === undefined || value === "") {
        return "N/A";
      }

      return String(value);
    },
  },
};
</script>

<style scoped>
.snapshot-title {
  font-size: 0.95rem;
  font-weight: 700;
}

.snapshot-error {
  font-size: 0.76rem;
  margin-bottom: 0.35rem;
}

.snapshot-scroll-wrap {
  overflow-x: auto;
  overflow-y: hidden;
}

.snapshot-row {
  display: flex;
  gap: 0.5rem;
  min-width: 980px;
}

.snapshot-tile {
  flex: 1 1 0;
  min-width: 170px;
  border: 1px solid #e9ecef;
  border-radius: 0.55rem;
  padding: 0.45rem 2.55rem 0.45rem 0.55rem;
  background: #f8f9fa;
  position: relative;
  overflow: hidden;
}

.snapshot-tile::after {
  content: "";
  position: absolute;
  top: 50%;
  right: -18px;
  width: 52px;
  height: 52px;
  border-radius: 999px;
  transform: translateY(-50%);
  background: radial-gradient(circle, rgba(99, 102, 241, 0.12) 0%, rgba(99, 102, 241, 0) 72%);
  pointer-events: none;
}

.tile-label {
  font-size: 0.68rem;
  font-weight: 700;
  color: #6c757d;
  text-transform: uppercase;
  margin-bottom: 0.2rem;
}

.tile-value {
  font-size: 0.9rem;
  font-weight: 700;
  line-height: 1.2;
  margin-bottom: 0.15rem;
}

.tile-subtext {
  font-size: 0.74rem;
  color: #495057;
  line-height: 1.25;
}

.tile-icon-shell {
  --icon-fg: #475569;
  --icon-border: rgba(148, 163, 184, 0.38);
  --icon-bg-a: rgba(148, 163, 184, 0.24);
  --icon-bg-b: rgba(226, 232, 240, 0.34);
  position: absolute;
  right: 0.55rem;
  top: 50%;
  transform: translateY(-50%);
  width: 1.95rem;
  height: 1.95rem;
  border-radius: 999px;
  border: 1px solid var(--icon-border);
  background: linear-gradient(145deg, var(--icon-bg-a), var(--icon-bg-b));
  color: var(--icon-fg);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.68), 0 3px 8px rgba(15, 23, 42, 0.08);
  pointer-events: none;
}

.tile-icon-shell i {
  font-size: 0.9rem;
  line-height: 1;
}

.tile-icon-tone-revenue {
  --icon-fg: #0f766e;
  --icon-border: rgba(45, 212, 191, 0.45);
  --icon-bg-a: rgba(94, 234, 212, 0.28);
  --icon-bg-b: rgba(204, 251, 241, 0.42);
}

.tile-icon-tone-best {
  --icon-fg: #92400e;
  --icon-border: rgba(251, 191, 36, 0.52);
  --icon-bg-a: rgba(251, 191, 36, 0.25);
  --icon-bg-b: rgba(254, 240, 138, 0.38);
}

.tile-icon-tone-worst {
  --icon-fg: #b91c1c;
  --icon-border: rgba(248, 113, 113, 0.55);
  --icon-bg-a: rgba(248, 113, 113, 0.24);
  --icon-bg-b: rgba(254, 202, 202, 0.4);
}

.tile-icon-tone-day {
  --icon-fg: #1d4ed8;
  --icon-border: rgba(96, 165, 250, 0.5);
  --icon-bg-a: rgba(96, 165, 250, 0.24);
  --icon-bg-b: rgba(191, 219, 254, 0.4);
}

.tile-icon-tone-hour {
  --icon-fg: #5b21b6;
  --icon-border: rgba(167, 139, 250, 0.55);
  --icon-bg-a: rgba(167, 139, 250, 0.22);
  --icon-bg-b: rgba(221, 214, 254, 0.4);
}

.skeleton-line {
  height: 0.65rem;
  background: linear-gradient(90deg, #eceff1 20%, #dde2e6 40%, #eceff1 60%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.2s linear infinite;
  border-radius: 0.3rem;
  margin-bottom: 0.28rem;
}

.skeleton-title {
  width: 55%;
}

.skeleton-value {
  width: 80%;
  height: 0.85rem;
}

.skeleton-sub {
  width: 92%;
}

.skeleton-sub.short {
  width: 65%;
}

.snapshot-modal-body {
  max-height: 66vh;
  overflow-y: auto;
}

.info-section {
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
  padding: 0.7rem;
  margin-bottom: 0.7rem;
  background: #f8f9fa;
}

@keyframes skeleton-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

@media (max-width: 768px) {
  .snapshot-row {
    min-width: 840px;
  }

  .snapshot-tile {
    min-width: 155px;
  }
}
</style>
